import React, { useEffect, useState } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { Tag } from '../common/Tag';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { adminDashboardServices } from '../../APIServices/adminDashboardServices';

function AdminModule() {
  const { state, reset } = useData();
  const toast = useToast();
  const [confirmReset, setConfirmReset] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summary, setSummary] = useState(null);

  const loadSummary = async () => {
    try {
      setLoadingSummary(true);
      const data = await adminDashboardServices.getAdminDashboardSummary();
      setSummary(data?.data || null);
    } catch (error) {
      setSummary(null);
      toast('Unable to load admin summary. Showing local values.', 'warn');
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const engineConfig = summary?.engineConfig || {
    engineVersion: 'v2.3.1',
    mode: 'deterministic',
    rulePackVersion: 'v14',
    rulePackUpdatedAt: '14 May 2026',
    templateVersion: 'v8',
    templateTrack: 'BABE Feasibility primary',
    embeddingModel: 'BGE-large-en',
    embeddingMode: 'self-hosted',
    searchBackend: 'BM25 + dense vectors',
    searchMode: 'hybrid',
    externalCalls: 'None - private deployment',
  };

  const validationEvidence = summary?.validationEvidence || {
    iqOqPq: 'Complete',
    cfrPart11: 'Documented',
    determinism: 'Passing',
    determinismStats: '247/247',
    lastValidationRun: '22 May 2026',
    nextPeriodicReview: '22 Nov 2026',
  };

  const systemHealth = summary?.systemHealth || {
    apiUptime: 'N/A',
    avgQueryLatencyMs: 'N/A',
    storageUsedGb: 0,
    storageCapacityGb: 100,
    indexedDocuments: state.documents.length,
    auditLogSize: state.audit.length,
    lastBackup: 'Automated',
  };

  const masterData = summary?.masterData || {
    studiesRegistered: state.studies.length,
    documentsIndexed: state.documents.length,
    documentsSoftDeleted: state.documents.filter((d) => d.deleted).length,
    knowledgeItems: state.knowledge.length,
    knowledgeApproved: state.knowledge.filter((k) => k.status === 'approved').length,
    feasibilityRecords: state.feasibilities.length,
    riskRegister: state.risks.length,
    reportsGenerated: state.reports.length,
    users: state.users.length,
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">System administration</div>
          <h1 className="page-title">Admin</h1>
          <p className="page-sub">Master data, engine configuration, system health and validation evidence.</p>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className="section-label mb-5">Engine configuration</div>
          <div className="card card-pad">
            <table style={{ width: '100%', fontSize: 12 }}>
              <tbody>
                <tr><td className="muted" style={{ padding: 6, width: '40%' }}>Engine version</td><td><span className="mono">{engineConfig.engineVersion}</span> · {engineConfig.mode}</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Rule pack</td><td><span className="mono">{engineConfig.rulePackVersion}</span> · last updated {engineConfig.rulePackUpdatedAt}</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Template version</td><td><span className="mono">{engineConfig.templateVersion}</span> · {engineConfig.templateTrack}</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Embedding model</td><td>{engineConfig.embeddingModel} · {engineConfig.embeddingMode}</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Search backend</td><td>{engineConfig.searchBackend} · {engineConfig.searchMode}</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>External calls</td><td><Tag variant="ok" dot>{engineConfig.externalCalls}</Tag></td></tr>
              </tbody>
            </table>
          </div>

          <div className="section-label mt-4 mb-5">Validation evidence</div>
          <div className="card card-pad">
            <table style={{ width: '100%', fontSize: 12 }}>
              <tbody>
                <tr><td className="muted" style={{ padding: 6 }}>IQ / OQ / PQ</td><td><Tag variant="ok" dot>{validationEvidence.iqOqPq}</Tag></td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>21 CFR Part 11 assessment</td><td><Tag variant="ok" dot>{validationEvidence.cfrPart11}</Tag></td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Engine determinism tests</td><td><Tag variant="ok" dot>{validationEvidence.determinism} · {validationEvidence.determinismStats}</Tag></td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Last validation run</td><td>{validationEvidence.lastValidationRun}</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Next periodic review</td><td>{validationEvidence.nextPeriodicReview}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="col">
          <div className="section-label mb-5">System health</div>
          <div className="card card-pad">
            <table style={{ width: '100%', fontSize: 12 }}>
              <tbody>
                <tr><td className="muted" style={{ padding: 6, width: '40%' }}>API uptime</td><td className="num">{systemHealth.apiUptime}</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Avg query latency</td><td className="num">{systemHealth.avgQueryLatencyMs} ms</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Storage used</td><td className="num">{systemHealth.storageUsedGb} GB / {systemHealth.storageCapacityGb} GB</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Indexed documents</td><td className="num">{systemHealth.indexedDocuments}</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Audit log size</td><td className="num">{systemHealth.auditLogSize} events</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Last backup</td><td>{systemHealth.lastBackup}</td></tr>
              </tbody>
            </table>
          </div>

          <div className="section-label mt-4 mb-5">Master data</div>
          <div className="card card-pad">
            <table style={{ width: '100%', fontSize: 12 }}>
              <tbody>
                <tr><td className="muted" style={{ padding: 6 }}>Studies registered</td><td className="num">{masterData.studiesRegistered}</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Documents indexed</td><td className="num">{masterData.documentsIndexed} ({masterData.documentsSoftDeleted} soft-deleted)</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Knowledge items</td><td className="num">{masterData.knowledgeItems} ({masterData.knowledgeApproved} approved)</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Feasibility records</td><td className="num">{masterData.feasibilityRecords}</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Risk register</td><td className="num">{masterData.riskRegister}</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Reports generated</td><td className="num">{masterData.reportsGenerated}</td></tr>
                <tr><td className="muted" style={{ padding: 6 }}>Users</td><td className="num">{masterData.users}</td></tr>
              </tbody>
            </table>
          </div>

          <div className="section-label mt-4 mb-5">Maintenance</div>
          <div className="card card-pad">
            <div className="xs muted mb-3">Clear local runtime cache and reload data from live APIs.</div>
            <div className="xs muted mb-3">{loadingSummary ? 'Refreshing admin summary...' : `Last summary refresh: ${summary?.generatedAt ? new Date(summary.generatedAt).toLocaleString() : 'not available'}`}</div>
            <button className="btn btn-ghost" style={{ marginRight: 8 }} onClick={loadSummary}>
              <i className="ti ti-rotate-clockwise" /> Refresh Summary
            </button>
            <button className="btn btn-accent" onClick={() => setConfirmReset(true)}>
              <i className="ti ti-refresh" /> Clear local cache
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={async () => {
          await reset();
          await loadSummary();
          toast('Local cache cleared and admin summary refreshed', 'ok');
          setConfirmReset(false);
        }}
        title="Clear local runtime cache?"
        message="Locally cached runtime values will be cleared. Modules will re-fetch data from APIs on next load."
        confirmLabel="Clear"
        danger
      />
    </div>
  );
}

export default AdminModule;